import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { BookOpen, Check, Trash2, ExternalLink, Filter } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin queue for book submissions. Mounted on /profile.
 * Uses GET /api/admin/books + PATCH /api/admin/books/:id.
 */
export default function AdminBookQueue() {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async (s = status) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/books", { params: { status: s, limit: 200 } });
      setRows(data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load books"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(status); /* eslint-disable-next-line */ }, [status]);

  const approve = async (r) => {
    try {
      await api.patch(`/admin/books/${r.id}`, { status: "approved" });
      setRows((arr) => arr.filter((x) => x.id !== r.id));
      toast.success("Approved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not approve"); }
  };
  const reject = async (r) => {
    if (!window.confirm("Reject this submission?")) return;
    try {
      await api.patch(`/admin/books/${r.id}`, { status: "rejected" });
      setRows((arr) => arr.filter((x) => x.id !== r.id));
      toast.success("Rejected");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not reject"); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.title}"?`)) return;
    try {
      await api.delete(`/books/${r.id}`);
      setRows((arr) => arr.filter((x) => x.id !== r.id));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  };

  return (
    <div className="jj-card mt-6 p-6 sm:p-7 border-burgundy/30 bg-gradient-to-br from-cream to-cream-surface" data-testid="admin-book-queue">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="max-w-xl">
          <div className="overline text-burgundy flex items-center gap-2"><BookOpen className="h-3.5 w-3.5" /> Admin · Book submissions</div>
          <h2 className="font-serif text-2xl text-jjblue mt-1">
            {loading ? "Loading…" : status === "pending" ? `${rows.length} awaiting review.` : `${rows.length} book${rows.length === 1 ? "" : "s"}.`}
          </h2>
          <p className="mt-1.5 text-sm text-[var(--jj-text-secondary)]">
            Approve, reject, or delete book submissions from members before they land on the Book Club wall.
          </p>
        </div>
        <div className="inline-flex bg-cream-surface rounded-full p-1 text-xs">
          {["pending", "approved", "rejected", "all"].map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              className={`px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 ${status === s ? "bg-burgundy text-cream" : "text-jjblue/70"}`}
              data-testid={`admin-book-tab-${s}`}
            >
              <Filter className="h-3 w-3" /> {s[0].toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5">
        {loading ? (
          <div className="text-center text-[var(--jj-muted)] py-6 text-sm">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-center text-[var(--jj-muted)] py-6 text-sm" data-testid="admin-book-empty">
            {status === "pending" ? "All caught up — no submissions waiting." : "Nothing matches this filter."}
          </div>
        ) : (
          <ul className="space-y-2" data-testid="admin-book-list">
            {rows.map((r) => (
              <li key={r.id} className="jj-card bg-white p-4 flex items-start gap-3 flex-wrap" data-testid={`admin-book-row-${r.id}`}>
                <div className="h-16 w-11 rounded-md overflow-hidden shrink-0 bg-cream-surface">
                  {r.cover_url ? <img src={r.cover_url} alt="" className="w-full h-full object-cover" /> : (
                    <div className="w-full h-full flex items-center justify-center text-burgundy/40"><BookOpen className="h-5 w-5" /></div>
                  )}
                </div>
                <div className="flex-1 min-w-[220px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-wider bg-jjblue/10 text-jjblue px-2 py-0.5 rounded-full">{r.format}</span>
                    <span className="text-[11px] text-[var(--jj-muted)]">{r.category}</span>
                  </div>
                  <div className="mt-1 font-medium text-jjblue">{r.title}</div>
                  <div className="text-xs text-[var(--jj-muted)]">by {r.author} · uploaded by {r.uploader_name}</div>
                  {r.description && <p className="mt-1 text-xs text-jjblue/85 line-clamp-2">{r.description}</p>}
                  {r.file_url && (
                    <a href={r.file_url} target="_blank" rel="noopener noreferrer" className="mt-1 inline-flex items-center gap-1 text-[11px] text-burgundy hover:underline">
                      Preview link <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {r.status === "pending" && (
                    <>
                      <button onClick={() => approve(r)} className="btn-primary !py-1.5 !px-3 text-xs inline-flex items-center gap-1" data-testid={`admin-book-approve-${r.id}`}>
                        <Check className="h-3.5 w-3.5" /> Approve
                      </button>
                      <button onClick={() => reject(r)} className="btn-secondary !py-1.5 !px-3 text-xs" data-testid={`admin-book-reject-${r.id}`}>Reject</button>
                    </>
                  )}
                  <button onClick={() => remove(r)} className="p-1.5 text-[var(--jj-muted)] hover:text-burgundy" title="Delete" data-testid={`admin-book-delete-${r.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
